import { getAllParticipants, getParticipantStepCount, isLeadBooker } from './booking';
import { getParticipantAnswers, isAnswerEmpty, isCustomField, isSameAnswer } from './precheckinAnswers';

// Centralized logic for determining participant step status
export const getParticipantStepStatus = (
    participant,
    fields,
    formData = null,
    booking = null,
    preCheckSettings = null,
    wasSavedInSession = false
) => {
    if (!participant) return 'incomplete';

    // Check if participant is already pre-checked-in
    // For Lead Booker, check booking.checked_in; for party members, check participant.checked_in
    const isLeadBookerParticipant = participant.user?.id === booking?.user?.id;
    const isAlreadyCheckedIn = isLeadBookerParticipant
        ? booking?.checked_in
        : participant?.checked_in;

    if (isAlreadyCheckedIn) {
        return 'completed';
    }

    // Check if participant is skipped based on backend data
    const isSkipped = getParticipantSkippedStatus(participant, booking);
    if (isSkipped) {
        return 'skipped';
    }

    // If we have formData (current editing), check for changes and completeness
    if (formData) {
        const hasUnsavedChanges = hasFormDataChanges(participant, fields, formData);
        const hasAllRequiredDataInForm = hasAllRequiredFieldsInFormData(formData, fields, participant);

        if (hasUnsavedChanges) {
            if (hasAllRequiredDataInForm) return 'unsaved';
            const requiredFields = fields.filter(f => f.required || f.mandatory);
            return requiredFields.length > 0 ? 'missing_required' : 'incomplete';
        }
    }

    // If participant was not saved in current session, check backend saved flag
    if (!wasSavedInSession) {
        // Check if participant was saved in previous sessions (backend flag)
        const isLeadBookerParticipant = participant.user?.id === booking?.user?.id;
        const backendSaved = isLeadBookerParticipant
            ? booking?.saved
            : participant?.saved;

        // If not saved in backend either, check for missing required fields
        if (!backendSaved) {
            const hasAllRequired = hasAllRequiredFields(participant, fields);
            const requiredFields = fields.filter(f => f.required || f.mandatory);
            return (!hasAllRequired && requiredFields.length > 0) ? 'missing_required' : 'incomplete';
        }

        // If saved in backend, check if all required fields are filled
        const hasAllRequiredData = hasAllRequiredFields(participant, fields);
        if (!hasAllRequiredData) {
            return 'missing_required';
        }

        // If saved in backend and has all required data, check waivers
        const waiverStatus = getParticipantWaiverStatus(participant, booking, preCheckSettings);
        const hasSignedWaivers = waiverStatus === 'Pre-Checked-in';
        return hasSignedWaivers ? 'completed' : 'saved';
    }

    // Check waiver status for participants saved in current session
    const waiverStatus = getParticipantWaiverStatus(participant, booking, preCheckSettings);
    const hasSignedWaivers = waiverStatus === 'Pre-Checked-in';

    // Check if all required fields are filled — missing_required takes priority over saved
    const hasAllRequiredData = hasAllRequiredFields(participant, fields);
    const requiredFields = fields.filter(f => f.required || f.mandatory);
    if (!hasAllRequiredData && requiredFields.length > 0) {
        return 'missing_required';
    }

    // Return completed only if both saved in session and waivers are done
    return hasSignedWaivers ? 'completed' : 'saved';
};

// Check if participant is skipped based on backend data
const getParticipantSkippedStatus = (participant, booking) => {
    if (!participant || !booking) {
        return false;
    }

    // Check if this is Lead Booker (has user.id matching booking.user.id)
    const isLeadBookerParticipant = participant.user?.id === booking.user?.id;

    if (isLeadBookerParticipant) {
        return booking.check_in_skipped === true;
    } else {
        return participant.check_in_skipped === true;
    }
};

// Calculate participant waiver status
const getParticipantWaiverStatus = (participant, booking, preCheckSettings) => {
    if (!participant || !booking) {
        return 'Not pre-checked-in';
    }

    // Check if this is Lead Booker (has user.id matching booking.user.id)
    const isLeadBookerParticipant = participant.user?.id === booking.user?.id;

    // First check the checked_in flag (set by preCheckin API)
    if (isLeadBookerParticipant) {
        if (booking.checked_in) {
            return 'Pre-Checked-in';
        }
    } else {
        if (participant.checked_in) {
            return 'Pre-Checked-in';
        }
    }

    // Fallback to waiver signatures check if checked_in flag is not set
    if (!preCheckSettings?.waivers || !booking?.waiver_signatures) {
        return 'Not pre-checked-in';
    }

    const requiredWaivers = preCheckSettings.waivers;

    const participantSignatures = booking.waiver_signatures.filter(signature => {
        if (isLeadBookerParticipant) {
            // For Lead Booker, use is_lead_booker flag
            return signature.is_lead_booker === true && signature.agreed;
        } else {
            // For other participants, use participant field and ensure it's not Lead Booker
            return signature.participant === participant.id && signature.is_lead_booker !== true && signature.agreed;
        }
    });

    // Check if participant has signed all required waivers
    const hasAllSignatures = requiredWaivers.every(waiver =>
        participantSignatures.some(signature => signature.waiver === waiver.id)
    );

    return hasAllSignatures ? 'Pre-Checked-in' : 'Not pre-checked-in';
};

// Check if all participants are ready for pre-checkin (either completed, saved, or skipped)
// NOTE: This function assumes no participants were saved in current session
// Use useParticipantStatuses.areAllReady() for session-aware checks
export const areAllParticipantsReady = (booking, preCheckSettings) => {
    if (!booking || !preCheckSettings) return false;

    const allParticipants = getAllParticipants(booking);
    const n = getParticipantStepCount(booking, preCheckSettings);

    return allParticipants.slice(0, n).every((participant, index) => {
        const fields = isLeadBooker(index)
            ? preCheckSettings.lead_booker_fields || []
            : preCheckSettings.party_member_fields || [];

        // Pass false for wasSavedInSession since this is a legacy function
        const status = getParticipantStepStatus(participant, fields, null, booking, preCheckSettings, false);
        // Lead Booker cannot be skipped — must be completed or saved
        if (isLeadBooker(index)) {
            return status === 'completed' || status === 'saved';
        }
        return status === 'completed' || status === 'saved' || status === 'skipped';
    });
};

// Check if all participants are already pre-checked-in via checked_in flags
export const areAllParticipantsCheckedIn = (booking, preCheckSettings) => {
    if (!booking) return false;

    if (!booking.checked_in) {
        return false;
    }

    if (preCheckSettings?.collect_party_members_info === false) {
        return true;
    }

    const maxGuests = Math.max(0, getParticipantStepCount(booking, preCheckSettings) - 1);
    const party = booking.party || [];
    if (party.slice(0, maxGuests).some((participant) => !participant.checked_in)) {
        return false;
    }

    return true;
};

// Check if current form data differs from participant data
const hasFormDataChanges = (participant, fields, formData) => {
    for (const field of fields) {
        const formValue = formData[field.id];
        let participantValue;

        // Custom fields: compared with the stored answer (precheckin_answers)
        if (isCustomField(field)) {
            if (!isSameAnswer(field, formValue, getParticipantAnswers(participant)[field.id])) {
                return true;
            }
            continue;
        }

        // Get participant value based on field type
        switch (field.id) {
            case 'first_name':
            case 'last_name':
            case 'email':
            case 'date_of_birth':
            case 'address':
                participantValue = participant.user?.[field.id] || '';
                break;
            case 'phone':
                participantValue = participant.user?.phone || '';
                break;
            case 'passport_number':
            case 'emergency_contact':
            case 'custom_note':
                participantValue = participant[field.id] || participant.user?.[field.id] || '';
                break;
            case 'is_minor':
                participantValue = isStoredMinor(participant);
                break;
            default:
                if (field.type === 'tag_category') {
                    const selectedTagIds = (participant.tags || [])
                        .filter(tag => tag.category?.code === field.id)
                        .map(tag => tag.id);
                    participantValue = selectedTagIds;
                } else {
                    participantValue = participant[field.id] || '';
                }
                break;
        }

        // Compare values
        if (Array.isArray(formValue) && Array.isArray(participantValue)) {
            if (formValue.length !== participantValue.length ||
                !formValue.every(v => participantValue.includes(v))) {
                return true;
            }
        } else if (formValue !== participantValue) {
            return true;
        }
    }

    return false;
};

const isStoredMinor = (participant) => (
    participant.is_minor !== undefined ? participant.is_minor : (participant.user?.is_minor || false)
);

// CP-8118: a minor's email and phone are hidden in the form, so they are never required
const isMinorExemptField = (fieldId) => fieldId === 'email' || fieldId === 'phone';

// Check if participant has all required fields filled
const hasAllRequiredFields = (participant, fields) => {
    const isMinor = isStoredMinor(participant);
    for (const field of fields) {
        if (!field.required && !field.mandatory) continue;

        // Skip boolean fields - they always have a value (true/false)
        if (field.type === 'boolean') {
            continue;
        }

        if (isMinor && isMinorExemptField(field.id)) {
            continue;
        }

        // Custom fields: answered when precheckin_answers holds a value (0 counts)
        if (isCustomField(field)) {
            if (isAnswerEmpty(getParticipantAnswers(participant)[field.id])) {
                return false;
            }
            continue;
        }

        let value;

        switch (field.id) {
            case 'first_name':
            case 'last_name':
            case 'email':
            case 'date_of_birth':
            case 'address':
                value = participant.user?.[field.id];
                break;
            case 'phone':
                value = participant.user?.phone;
                break;
            case 'passport_number':
            case 'emergency_contact':
            case 'custom_note':
                value = participant[field.id] || participant.user?.[field.id];
                break;
            case 'is_minor':
                continue; // is_minor is never required itself
            default:
                if (field.type === 'tag_category') {
                    const selectedTags = (participant.tags || [])
                        .filter(tag => tag.category?.code === field.id);
                    value = selectedTags.length > 0 ? selectedTags : null;
                } else {
                    value = participant[field.id];
                }
                break;
        }

        // Check if required field is empty
        if (field.type === 'tag_category') {
            if (!value || (Array.isArray(value) && value.length === 0)) {
                return false;
            }
        } else {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                return false;
            }
        }
    }

    return true;
};

// Check if form data has all required fields filled
const hasAllRequiredFieldsInFormData = (formData, fields, participant) => {
    // The box ticked in the open form, as DynamicFields and ParticipantStep read it; the stored flag if the form has none
    const isMinor = formData.is_minor !== undefined ? !!formData.is_minor : isStoredMinor(participant);
    for (const field of fields) {
        if (!field.required && !field.mandatory) continue;

        // Skip boolean fields - they always have a value (true/false)
        if (field.type === 'boolean') {
            continue;
        }

        let value = formData[field.id];

        if (isMinor && isMinorExemptField(field.id)) {
            continue;
        }

        // Skip is_minor as it's never required itself
        if (field.id === 'is_minor') {
            continue;
        }

        // Check if required field is empty
        if (field.type === 'tag_category') {
            if (!value || (Array.isArray(value) && value.length === 0)) {
                return false;
            }
        } else {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                return false;
            }
        }
    }

    return true;
};

export const getStatusIcon = (status) => {
    switch (status) {
        case 'completed':
            return 'check_circle'; // Green checkmark
        case 'unsaved':
            return 'error'; // Red dot  
        case 'saved':
            return 'radio_button_checked'; // Blue dot
        case 'skipped':
            return 'radio_button_checked'; // Gray solid dot
        case 'missing_required':
            return 'fiber_manual_record'; // Solid dot (red-ish)
        case 'incomplete':
        default:
            return 'radio_button_unchecked'; // Empty circle
    }
};

export const getStatusColor = (status) => {
    switch (status) {
        case 'completed':
            return 'success.main';
        case 'unsaved':
            return 'error.main';
        case 'saved':
            return 'primary.main';
        case 'skipped':
            return 'text.disabled';
        case 'missing_required':
            return 'rgba(254, 63, 53, 0.5)';
        case 'incomplete':
        default:
            return 'text.disabled';
    }
}; 